import {
  ChevronLeft,
  ChevronRight,
  History,
  Layers,
  Redo2,
  SlidersHorizontal,
  Undo2,
} from 'lucide-react';
import { lazy, Suspense } from 'react';
import { formatShortcut } from '../core/input/shortcuts';
import {
  checkDocumentSize,
  formatBytes,
  HISTORY_LIMIT,
  HISTORY_MEMORY_BUDGET,
  MAX_DIMENSION,
} from '../core/limits';
import { type RightPanelId, useEditorStore } from '../state/editorStore';
import { Button, Field, NumberField, PanelSection } from './components/controls';

/**
 * The Layers and Adjust panels are loaded when they are first opened.
 *
 * The editor opens on the Canvas tab, so neither panel is needed to put the
 * canvas on screen — and the Layers panel in particular grew with the layer
 * tree (Iteration 11). The boot chunk is the one number the bundle budget
 * watches, so the panels that are not on screen at boot do not belong in it.
 */
const LayersPanel = lazy(() =>
  import('./LayersPanel').then((module) => ({ default: module.LayersPanel })),
);
const AdjustPanel = lazy(() =>
  import('./AdjustPanel').then((module) => ({ default: module.AdjustPanel })),
);

const TABS: { id: RightPanelId; label: string; icon: typeof Layers; plannedFor?: number }[] = [
  { id: 'layers', label: 'Layers', icon: Layers },
  { id: 'adjust', label: 'Adjust', icon: SlidersHorizontal },
  { id: 'document', label: 'Canvas', icon: Layers },
  { id: 'history', label: 'History', icon: History },
];

/**
 * The right panel, and the two affordances that give its width back to the
 * canvas (Iteration 13).
 *
 * Closed is a layout fact, not an unmount: the aside keeps rendering with
 * `w-0 overflow-hidden` + `inert`, so the lazy panels' chunks and scroll
 * positions survive a toggle. The width snaps — no animation — because the
 * engine's ResizeObserver would re-render the canvas every frame of a
 * transition (see DESIGN-iteration-13.md §4).
 */
export function RightPanel() {
  const panel = useEditorStore((s) => s.rightPanel);
  const setPanel = useEditorStore((s) => s.setRightPanel);
  const open = useEditorStore((s) => s.rightPanelOpen);
  const openPanel = useEditorStore((s) => s.openRightPanel);
  const togglePanel = useEditorStore((s) => s.toggleRightPanel);
  const toggleKey = formatShortcut('mod+backslash');

  return (
    <>
      {/* The reopen strip: a sibling of the stage in the main row, not an
          overlay — honest layout, nothing occluded. */}
      {!open ? (
        <button
          type="button"
          onClick={() => openPanel()}
          title={`Show sidebar (${toggleKey})`}
          aria-label="Show sidebar"
          className="flex w-6 shrink-0 items-center justify-center border-l border-line bg-surface text-muted hover:bg-surface-2 hover:text-ink"
          data-testid="sidebar-reopen"
        >
          <ChevronLeft size={14} />
        </button>
      ) : null}
      <aside
        className={
          open
            ? 'flex w-72 shrink-0 flex-col border-l border-line bg-surface'
            : // The border must go with the width, or a 1 px line survives.
              'flex w-0 min-w-0 shrink-0 flex-col overflow-hidden border-l-0 bg-surface'
        }
        data-testid="right-panel"
        {...(open ? {} : { inert: true, 'aria-hidden': 'true' as const })}
      >
        <div className="flex shrink-0 items-stretch border-b border-line">
          <div role="tablist" aria-label="Panels" className="flex flex-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                type="button"
                aria-selected={panel === tab.id}
                disabled={tab.plannedFor !== undefined}
                title={
                  tab.plannedFor ? `Planned for Iteration ${tab.plannedFor}` : `Show ${tab.label}`
                }
                onClick={() => setPanel(tab.id)}
                className={`flex-1 border-b-2 px-1 py-2 text-[11px] ${
                  panel === tab.id
                    ? 'border-accent text-ink'
                    : 'border-transparent text-muted hover:text-ink'
                } disabled:cursor-not-allowed disabled:opacity-40`}
                data-testid={`panel-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={togglePanel}
            title={`Hide sidebar (${toggleKey})`}
            aria-label="Hide sidebar"
            className="flex w-7 items-center justify-center border-l border-line text-muted hover:bg-surface-2 hover:text-ink"
            data-testid="sidebar-hide"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {panel === 'document' ? <DocumentPanel /> : null}
          {panel === 'history' ? <HistoryPanel /> : null}
          {panel === 'layers' ? (
            <Suspense fallback={<p className="p-3 text-[12px] text-muted">Loading…</p>}>
              <LayersPanel />
            </Suspense>
          ) : null}
          {panel === 'adjust' ? (
            <Suspense fallback={<p className="p-3 text-[12px] text-muted">Loading…</p>}>
              <AdjustPanel />
            </Suspense>
          ) : null}
        </div>
      </aside>
    </>
  );
}

function DocumentPanel() {
  const doc = useEditorStore((s) => s.doc);
  const updateDoc = useEditorStore((s) => s.updateDoc);
  const notify = useEditorStore((s) => s.notify);

  const resize = (width: number, height: number) => {
    const w = Math.round(width);
    const h = Math.round(height);
    const check = checkDocumentSize(w, h);
    if (!check.ok) {
      notify(check.reason ?? 'Invalid size', 'warn');
      return;
    }
    updateDoc('Resize canvas', (current) => ({ ...current, width: w, height: h }));
  };

  const transparent = doc.background === 'transparent';

  return (
    <>
      <PanelSection title="Document">
        <Field label="Name">
          <input
            aria-label="Document name"
            value={doc.name}
            onChange={(event) =>
              updateDoc('Rename', (current) => ({ ...current, name: event.target.value }))
            }
            className="w-40 rounded-md border border-line bg-surface-2 px-2 py-1 focus:border-accent focus:outline-none"
            data-testid="doc-name"
          />
        </Field>
        <Field label="Width">
          <NumberField
            min={1}
            max={MAX_DIMENSION}
            value={doc.width}
            onCommit={(value) => resize(value, doc.height)}
            data-testid="doc-width"
          />
        </Field>
        <Field label="Height">
          <NumberField
            min={1}
            max={MAX_DIMENSION}
            value={doc.height}
            onCommit={(value) => resize(doc.width, value)}
            data-testid="doc-height"
          />
        </Field>
        <p className="pt-1 text-[11px] text-faint">
          Canvas size changes the frame only; resampling existing pixels lands in Iteration 2.
        </p>
      </PanelSection>

      <PanelSection title="Background">
        <Field label="Colour">
          <input
            aria-label="Background colour picker"
            type="color"
            disabled={transparent}
            value={transparent ? '#ffffff' : doc.background}
            onChange={(event) =>
              updateDoc('Background colour', (current) => ({
                ...current,
                background: event.target.value,
              }))
            }
            className="size-7 cursor-pointer rounded border border-line bg-surface-2 disabled:opacity-40"
            data-testid="doc-background"
          />
          <input
            aria-label="Background colour value"
            type="text"
            disabled={transparent}
            value={transparent ? 'transparent' : doc.background}
            onChange={(event) =>
              updateDoc('Background colour', (current) => ({
                ...current,
                background: event.target.value,
              }))
            }
            className="w-24 rounded-md border border-line bg-surface-2 px-2 py-1 font-mono text-[11px] focus:border-accent focus:outline-none disabled:opacity-40"
          />
        </Field>
        <Field label="Transparent">
          <input
            aria-label="Transparent background"
            type="checkbox"
            checked={transparent}
            onChange={(event) =>
              updateDoc('Transparent background', (current) => ({
                ...current,
                background: event.target.checked ? 'transparent' : '#ffffff',
              }))
            }
            className="size-4 accent-(--color-accent)"
            data-testid="doc-transparent"
          />
        </Field>
      </PanelSection>
    </>
  );
}

function HistoryPanel() {
  const rows = useEditorStore((s) => s.historyRows);
  const historyBytes = useEditorStore((s) => s.historyBytes);
  const canUndo = useEditorStore((s) => s.canUndo);
  const canRedo = useEditorStore((s) => s.canRedo);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  // Newest first, like every history panel users know.
  const ordered = [...rows].reverse();

  return (
    <>
      <PanelSection
        title="History"
        actions={
          <>
            <Button onClick={() => void undo()} disabled={!canUndo} title="Undo (Ctrl+Z)">
              <Undo2 size={14} />
            </Button>
            <Button onClick={() => void redo()} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)">
              <Redo2 size={14} />
            </Button>
          </>
        }
      >
        <ol className="flex flex-col gap-px" data-testid="history-list">
          {ordered.map((row) => (
            <li
              key={row.id}
              className={`rounded px-2 py-1 ${
                row.active ? 'bg-accent-soft text-ink' : 'text-muted'
              } ${row.undone ? 'line-through opacity-60' : ''}`}
              data-active={row.active || undefined}
            >
              {row.label}
            </li>
          ))}
        </ol>
      </PanelSection>
      <PanelSection title="Memory">
        <p className="text-[11px] text-faint" data-testid="history-memory">
          {rows.length} of {HISTORY_LIMIT} steps kept · {formatBytes(historyBytes)} of{' '}
          {formatBytes(HISTORY_MEMORY_BUDGET)} used. Oldest steps are dropped when either limit runs
          out.
        </p>
      </PanelSection>
    </>
  );
}
